import {
  API_BASE_URL,
  getAuthHeaders,
  redirectToLoginWithReturn,
} from '../../../utils/utils';

const BASE = `${API_BASE_URL}/api/toefl`;

/** Point relative `/uploads/…` audio at the API host, not Firebase. */
export function resolveToeflMediaUrl(url) {
  if (!url) return null;
  const raw = String(url);
  if (/^https?:\/\//i.test(raw) || raw.startsWith('blob:') || raw.startsWith('data:')) {
    return raw;
  }
  const origin = String(API_BASE_URL || '').replace(/\/$/, '');
  const path = raw.startsWith('/') ? raw : `/${raw}`;
  return `${origin}${path}`;
}

function authHeaders(extra = {}) {
  const headers = getAuthHeaders();
  if (extra['Content-Type'] === undefined) {
    delete headers['Content-Type'];
  }
  return { ...headers, ...extra };
}

async function parseJson(response) {
  if (response.status === 401) {
    redirectToLoginWithReturn({ publishLogoutReason: 'session_expired' });
    throw new Error('unauthorized');
  }
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const detail = payload?.detail || payload?.message || `HTTP ${response.status}`;
    const message = typeof detail === 'string'
      ? detail
      : (detail?.message || JSON.stringify(detail));
    const error = new Error(message);
    error.status = response.status;
    error.payload = payload;
    error.code = typeof detail === 'object' ? detail?.code : undefined;
    throw error;
  }
  return payload;
}

export async function fetchToeflSimulations() {
  const response = await fetch(`${BASE}/simulations`, {
    headers: authHeaders({ 'Content-Type': 'application/json' }),
  });
  return parseJson(response);
}

export async function fetchToeflItem(itemId) {
  if (!itemId) return null;
  const response = await fetch(`${BASE}/items/${encodeURIComponent(itemId)}`, {
    headers: authHeaders({ 'Content-Type': 'application/json' }),
  });
  return parseJson(response);
}

export async function finishToeflTest(payload) {
  const response = await fetch(`${BASE}/finish-test`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload),
  });
  return parseJson(response);
}

export async function submitToeflSpeaking(payload) {
  const isForm = typeof FormData !== 'undefined' && payload instanceof FormData;
  const response = await fetch(`${BASE}/speaking/submit`, {
    method: 'POST',
    headers: isForm ? authHeaders() : authHeaders({ 'Content-Type': 'application/json' }),
    body: isForm ? payload : JSON.stringify(payload),
  });
  return parseJson(response);
}

export async function pollToeflSpeakingRecovery(testId) {
  if (!testId) return null;
  const response = await fetch(
    `${BASE}/speaking/submit/recovery?testId=${encodeURIComponent(testId)}`,
    { headers: authHeaders({ 'Content-Type': 'application/json' }) },
  );
  if (response.status === 404) return null;
  return parseJson(response);
}

export async function fetchToeflReadiness() {
  const response = await fetch(`${BASE}/readiness`, {
    headers: authHeaders({ 'Content-Type': 'application/json' }),
  });
  return parseJson(response);
}

export async function activateToeflBootcamp(body) {
  const examDate = body?.exam_date || body?.target_date;
  const response = await fetch(`${BASE}/bootcamp/activate`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      ...body,
      exam_date: examDate,
      target_date: body?.target_date || examDate,
    }),
  });
  return parseJson(response);
}

export async function fetchToeflBootcamp() {
  const response = await fetch(`${BASE}/bootcamp`, {
    headers: authHeaders({ 'Content-Type': 'application/json' }),
  });
  return parseJson(response);
}

export async function retakeToeflDiagnostic() {
  const response = await fetch(`${BASE}/bootcamp/diagnostic/retake`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
  });
  return parseJson(response);
}

export async function cancelToeflBootcamp() {
  const response = await fetch(`${BASE}/bootcamp/cancel`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({}),
  });
  return parseJson(response);
}

export async function patchToeflExamDate(body) {
  const examDate = body?.exam_date || body?.target_date;
  const response = await fetch(`${BASE}/bootcamp/exam-date`, {
    method: 'PATCH',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      exam_date: examDate,
      target_date: body?.target_date || examDate,
    }),
  });
  return parseJson(response);
}

export async function fetchToeflDailyContent(options = {}) {
  const skill = options.skill ? `?skill=${encodeURIComponent(options.skill)}` : '';
  const response = await fetch(`${BASE}/daily-content${skill}`, {
    headers: authHeaders({ 'Content-Type': 'application/json' }),
  });
  return parseJson(response);
}

export async function completeToeflLesson(slug) {
  if (!slug) return null;
  const response = await fetch(`${BASE}/lessons/${encodeURIComponent(slug)}/complete`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
  });
  return parseJson(response);
}

export async function fetchTaskAttempt() {
  return null;
}
